import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import {
    Star,
    Loader2,
    Sparkles,
    ShieldAlert,
    Filter,
    Send,
    MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import ReplyProtocol from "@/pages/ReplyProtocol";
import Comments from "@/pages/Comments";
import { toast } from "sonner";

const RATING_COLORS = ["", "rose", "rose", "amber", "emerald", "emerald"];

function ratingClass(r) {
    return {
        rose: "text-rose-300 border-rose-500/30 bg-rose-500/10",
        amber: "text-amber-300 border-amber-500/30 bg-amber-500/10",
        emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
    }[RATING_COLORS[r]];
}

export default function Reputation() {
    const { currentBusiness, currentLocationId, featureFlags } = useApp();
    const commentsOn = featureFlags?.comments !== false;
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rating, setRating] = useState("all");
    const [sentiment, setSentiment] = useState("all");
    const [replyStatus, setReplyStatus] = useState("all");

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        const params = {};
        if (rating !== "all") params.rating = Number(rating);
        if (sentiment !== "all") params.sentiment = sentiment;
        if (replyStatus !== "all") params.reply_status = replyStatus;
        if (currentLocationId) params.location_id = currentLocationId;
        try {
            const { reviews: r } = await api.reviews(currentBusiness.id, params);
            setReviews(r || []);
        } catch {
            toast.error("Couldn't load reviews.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id, currentLocationId, rating, sentiment, replyStatus]);

    return (
        <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="reputation-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-indigo-300">Reputation</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-slate-50 tracking-tight">
                        Reviews · Reply Protocol{commentsOn ? " · Instagram Comments" : ""}
                    </h1>
                    <p className="mt-2 text-sm text-slate-400">
                        AI-analysed, guardrail-safe, provenance-labelled.
                    </p>
                </div>
            </div>

            <Tabs defaultValue="reviews" className="w-full">
                <TabsList className="bg-white/[0.03] border border-white/10">
                    <TabsTrigger value="reviews" data-testid="reputation-tab-reviews">
                        Reviews
                    </TabsTrigger>
                    <TabsTrigger value="protocol" data-testid="reputation-tab-protocol">
                        Reply Protocol
                    </TabsTrigger>
                    {commentsOn && (
                        <TabsTrigger value="comments" data-testid="reputation-tab-comments">
                            Instagram Comments
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="reviews" className="mt-6 space-y-6">
                    <div className="surface rounded-2xl p-4 flex items-center gap-3 flex-wrap">
                        <Filter className="h-4 w-4 text-slate-500" />
                        <FilterSelect
                            testid="review-filter-rating"
                            label="Rating"
                            value={rating}
                            onChange={setRating}
                            options={[
                                { v: "all", l: "All ratings" },
                                { v: "5", l: "5 ★" },
                                { v: "4", l: "4 ★" },
                                { v: "3", l: "3 ★" },
                                { v: "2", l: "2 ★" },
                                { v: "1", l: "1 ★" },
                            ]}
                        />
                        <FilterSelect
                            testid="review-filter-sentiment"
                            label="Sentiment"
                            value={sentiment}
                            onChange={setSentiment}
                            options={[
                                { v: "all", l: "All sentiment" },
                                { v: "positive", l: "Positive" },
                                { v: "neutral", l: "Neutral" },
                                { v: "negative", l: "Negative" },
                            ]}
                        />
                        <FilterSelect
                            testid="review-filter-reply-status"
                            label="Reply status"
                            value={replyStatus}
                            onChange={setReplyStatus}
                            options={[
                                { v: "all", l: "All" },
                                { v: "unreplied", l: "Unreplied" },
                                { v: "pending", l: "Pending approval" },
                                { v: "published", l: "Published" },
                            ]}
                        />
                        <div className="ml-auto text-xs text-slate-500 mono">
                            {reviews.length} reviews
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-16 text-slate-500">
                            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {reviews.map((r) => (
                                <ReviewCard key={r.id} r={r} onChange={load} />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="protocol" className="mt-6">
                    <ReplyProtocol />
                </TabsContent>

                {commentsOn && (
                    <TabsContent value="comments" className="mt-6">
                        <Comments />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

function FilterSelect({ testid, label, value, onChange, options }) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger
                data-testid={testid}
                className="w-[170px] bg-white/[0.03] border-white/10 text-slate-200 text-xs h-9"
            >
                <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent>
                {options.map((o) => (
                    <SelectItem key={o.v} value={o.v} data-testid={`${testid}-option-${o.v}`}>
                        {o.l}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function ReviewCard({ r, onChange }) {
    const [draft, setDraft] = useState(r.reply_draft || "");
    const [genBusy, setGenBusy] = useState(false);
    const [pending, setPending] = useState(r.reply_status === "pending");

    useEffect(() => {
        setDraft(r.reply_draft || "");
        setPending(r.reply_status === "pending");
    }, [r]);

    const generate = async () => {
        setGenBusy(true);
        try {
            const res = await api.genReply(r.id);
            setDraft(res.reply_text || "");
            if (res.status === "pending_approval") {
                toast.success("Draft sent to Approval Inbox");
                setPending(true);
            } else if (res.status === "published") {
                toast.success("Auto-published (Autopilot)");
            }
            onChange?.();
        } catch (e) {
            toast.error("Couldn't generate reply.");
        } finally {
            setGenBusy(false);
        }
    };

    return (
        <div
            data-testid={`review-card-${r.id}`}
            className="surface rounded-2xl p-5 space-y-4"
        >
            <div className="flex items-start gap-4">
                <img
                    src={r.reviewer_avatar}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover shrink-0 border border-white/10"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-100">
                            {r.reviewer_name}
                        </span>
                        <Badge
                            variant="outline"
                            data-testid={`review-rating-${r.id}`}
                            className={`gap-1 ${ratingClass(r.rating)}`}
                        >
                            <Star className="h-3 w-3" /> {r.rating}
                        </Badge>
                        {r.sentiment && (
                            <span className="text-[11px] mono uppercase tracking-widest text-slate-500">
                                {r.sentiment}
                            </span>
                        )}
                        {r.risk_flag && (
                            <Badge
                                data-testid={`review-risk-${r.id}`}
                                className="bg-rose-500/15 border border-rose-500/30 text-rose-200 gap-1"
                            >
                                <ShieldAlert className="h-3 w-3" /> Approval required
                            </Badge>
                        )}
                        {pending && (
                            <Badge
                                data-testid={`review-pending-${r.id}`}
                                className="bg-amber-500/15 border border-amber-500/30 text-amber-200"
                            >
                                Pending approval
                            </Badge>
                        )}
                        {r.reply_status === "published" && (
                            <Badge
                                data-testid={`review-published-${r.id}`}
                                className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-200"
                            >
                                Published
                            </Badge>
                        )}
                    </div>
                    <p className="mt-2 text-sm text-slate-200 leading-relaxed">
                        {r.text}
                    </p>
                    {r.complaint_category && (
                        <div className="mt-2 text-[11px] text-slate-500 mono uppercase tracking-widest">
                            Category: {r.complaint_category.replaceAll("_", " ")} · Severity:{" "}
                            {r.severity} · Urgency: {r.urgency}
                        </div>
                    )}
                    {r.risk_reasons?.length > 0 && (
                        <div className="mt-1 text-[11px] mono text-rose-300/80">
                            {r.risk_reasons.join(" · ")}
                        </div>
                    )}
                </div>
            </div>

            {r.published_reply ? (
                <div className="rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20 p-3">
                    <div className="label-eyebrow text-emerald-300 mb-1">
                        Published reply
                    </div>
                    <div className="text-sm text-slate-200 whitespace-pre-wrap">
                        {r.published_reply}
                    </div>
                </div>
            ) : (
                <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="label-eyebrow text-indigo-300 flex items-center gap-1.5">
                            <MessageSquare className="h-3 w-3" /> AI reply draft
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={generate}
                            disabled={genBusy || pending}
                            data-testid={`review-generate-reply-${r.id}`}
                            className="h-7 text-xs border-white/10 bg-white/5 hover:bg-white/10 gap-1"
                        >
                            {genBusy ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Sparkles className="h-3 w-3" />
                            )}
                            {draft ? "Regenerate" : "Generate reply"}
                        </Button>
                    </div>
                    <Textarea
                        data-testid={`review-reply-textarea-${r.id}`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="AI will draft a reply using your brand voice and approved claims."
                        rows={3}
                        className="bg-transparent border-white/5 text-sm text-slate-100 placeholder:text-slate-500"
                    />
                    {pending && (
                        <div className="mt-2 text-[11px] text-amber-300">
                            This reply is waiting in the Approval Inbox — approve or edit
                            it there to publish.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
